import { useMemo, type ReactElement } from 'react';
import {
  EditableRowTable,
  type EditableRow,
} from '@/components/editable-row-table';
import type { AllowBlockPolicyKey, PolicyRule } from '../types';
import { createEmptyRuleRow } from '../lib/adapters';
import {
  createAllowBlockValidateCell,
  isAllowBlockDraftEffectivelyEmpty,
} from '../lib/cellValidation';
import { getAllowBlockUiConfig } from '../config';

export interface PolicyRuleListProps {
  policyKey: AllowBlockPolicyKey;
  rules: PolicyRule[];
  /** Last saved snapshot for this list (allowed or blocked) — drives diff highlights. */
  baselineRules: PolicyRule[];
  onRulesChange: (next: PolicyRule[]) => void;
  readOnly: boolean;
  /** Support role: rows stay viewable/expandable, only commit actions are disabled. */
  isSupportReadOnly?: boolean;
  /** Committed rows + add-row draft validity (auto-save skips while invalid). */
  onValidationChange?: (valid: boolean) => void;
}

/**
 * Allow/block list for MCP (`mcp-server-sources`) or Extensions (`extensions-management`).
 * Composes generic {@link EditableRowTable} with policy-specific column config.
 */
export function PolicyRuleList({
  policyKey,
  rules,
  baselineRules,
  onRulesChange,
  readOnly,
  isSupportReadOnly = false,
  onValidationChange,
}: PolicyRuleListProps): ReactElement {
  const { columns } = useMemo(
    () => getAllowBlockUiConfig(policyKey),
    [policyKey]
  );

  const validateCell = useMemo(
    () => createAllowBlockValidateCell(policyKey),
    [policyKey]
  );

  const isDraftEmpty = useMemo(
    () => (row: EditableRow) =>
      isAllowBlockDraftEffectivelyEmpty(policyKey, row),
    [policyKey]
  );

  return (
    <EditableRowTable
      columns={columns}
      rows={rules}
      baselineRows={baselineRules}
      onRowsChange={onRulesChange}
      readOnly={readOnly}
      isSupportReadOnly={isSupportReadOnly}
      createEmptyRow={() => createEmptyRuleRow(policyKey)}
      validateCell={validateCell}
      onValidationChange={onValidationChange}
      isDraftEmpty={isDraftEmpty}
    />
  );
}
